import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  IconButton,
  Tooltip,
  Chip,
  Fade,
  Tabs,
  Tab
} from '@mui/material';
import { ArrowRightOutlined, EditOutlined, SaveOutlined, EyeOutlined, InfoOutlined } from '@ant-design/icons';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { getAsana } from 'services/asana/getAsana';
import { updateAsanaName } from 'services/asana/updateAsanaName';
import { getLibrary } from 'services/library/library';
import { importSpecificAsanaFromLibrary } from 'services/library/importSpecificAsanaFromLibrary';
import { importBulkAsana } from 'services/library/importBulkAsana';
import ImportAsanaDialogs from '../../components/openDialog/ImportAsanaDialogs';
import BulkImageImportDialog from '../../components/openDialog/BulkImageImportDialog';
import { FileUploadOutlined, SelfImprovementOutlined } from '@mui/icons-material';
import CreatePranayamaDialog from 'components/openDialog/CreatePranayama';
import ViewPranayamaPage from 'pages/pranayamma/ViewPranayamaPage';
import ViewRelaxationPage from 'pages/relaxation/ViewRelaxation';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`course-tabpanel-${index}`} aria-labelledby={`course-tab-${index}`} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `course-tab-${index}`,
    'aria-controls': `course-tabpanel-${index}`
  };
}

function ViewAsanaPage() {
  const navigate = useNavigate();
  const { courseid } = useParams();
  const coachId = Number(localStorage.getItem('userid'));
  const queryClient = useQueryClient();

  const [tabValue, setTabValue] = useState(0); // 0 = Asanas, 1 = Pranayammas

  // --- Local State ---
  const [postureNames, setPostureNames] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [asanaList, setAsanaList] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [dialogIndex, setDialogIndex] = useState(null);
  const [createMedOpen, setCreateMedOpen] = useState(false);

  // --- Queries ---
  const { data } = useQuery({
    queryKey: ['asanas', courseid],
    queryFn: () => getAsana({ courseId: Number(courseid) })
  });

  const {
    data: libraryData,
    isLoading: libraryLoading,
    isError: libraryError
  } = useQuery({
    queryKey: ['library', coachId],
    queryFn: () => getLibrary({ coachId }),
    retryOnMount: true
  });

  // --- Memos ---
  const asanas = useMemo(() => {
    return (
      data?.response.map((asana) => ({
        ...asana,
        postureId: asana.id,
        image: asana.libraryImage || asana.asanaImage || ''
      })) || []
    );
  }, [data]);

  // --- Effects ---
  useEffect(() => {
    if (data?.response) {
      const formatted = data.response.map((a) => ({
        ...a,
        postureId: a.id,
        image: a.libraryImage || a.asanaImage || ''
      }));
      setAsanaList((prev) =>
        prev.length > 0
          ? prev.map((existing) => {
              const updated = formatted.find((f) => f.postureId === existing.postureId);
              return updated ? { ...existing, ...updated } : existing;
            })
          : formatted
      );
      setPostureNames(formatted.map((p) => p.posturename || `Posture ${p.posturenumber || ''}`));
    }
  }, [data]);

  // --- Mutations ---
  const updatePostureNameMutation = useMutation({
    mutationFn: updateAsanaName,
    onSuccess: () => {
      setEditingIndex(null);
      toast.success('Posture name updated successfully');
    },
    onError: (err) => {
      console.error('Error updating posture name:', err);
      toast.error(err?.response?.data?.message || err.message || 'Unknown error');
    }
  });

  const updateSingleAsanaMutation = useMutation({
    mutationFn: ({ asanaId, libraryAsanaId, courseId }) => {
      console.log('Single asana mutation:', { asanaId, libraryAsanaId, courseId });
      return importSpecificAsanaFromLibrary({ asanaId, libraryAsanaId, courseId });
    },
    onSuccess: (data) => {
      toast.success('Asana updated successfully');
      const updatedAsana = data.response;
      setAsanaList((prev) =>
        prev.map((asana) =>
          asana.postureId === updatedAsana.postureid
            ? {
                ...asana,
                posturename: updatedAsana.posturename,
                asanaImage: updatedAsana.image,
                landmarks: updatedAsana.landmarks,
                posturenumber: updatedAsana.posturenumber,
                course_id: updatedAsana.course_id
              }
            : asana
        )
      );
      setPostureNames((prev) => prev.map((name, idx) => (asanas[idx].postureId === updatedAsana.postureid ? updatedAsana.posturename : name)));
      queryClient.setQueryData(['asanas', courseid], (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          response: oldData.response.map((asana) =>
            asana.id === updatedAsana.postureid
              ? {
                  ...asana,
                  posturename: updatedAsana.posturename,
                  asanaImage: updatedAsana.image,
                  landmarks: updatedAsana.landmarks,
                  posturenumber: updatedAsana.posturenumber,
                  course_id: updatedAsana.course_id
                }
              : asana
          )
        };
      });
      queryClient.invalidateQueries(['asanas', courseid], { refetchType: 'all' });
    },
    onError: (err) => {
      console.error('Single asana mutation error:', err);
      toast.error(err?.response?.data?.message || 'Failed to update asana');
    }
  });

  const importBulkAsanaMutation = useMutation({
    mutationFn: importBulkAsana,
    onSuccess: (data) => {
      toast.success(data.data?.message || 'Bulk import completed successfully');
      setAsanaList((prev) =>
        prev.map((asana) => {
          const result = data.data.results.find((r) => r.asanaId === asana.postureId);
          if (result?.success) {
            return {
              ...asana,
              posturename: result.updatedData.posturename,
              asanaImage: result.updatedData.image,
              landmarks: result.updatedData.landmarks,
              posturenumber: result.updatedData.posturenumber,
              course_id: result.updatedData.course_id
            };
          }
          return asana;
        })
      );
      setPostureNames((prev) =>
        prev.map((name, idx) => {
          const result = data.data.results.find((r) => r.asanaId === asanas[idx].postureId);
          return result?.success ? result.updatedData.posturename : name;
        })
      );
      queryClient.setQueryData(['asanas', courseid], (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          response: oldData.response.map((asana) => {
            const result = data.data.results.find((r) => r.asanaId === asana.id);
            if (result?.success) {
              return {
                ...asana,
                posturename: result.updatedData.posturename,
                asanaImage: result.updatedData.image,
                landmarks: result.updatedData.landmarks,
                posturenumber: result.updatedData.posturenumber,
                course_id: result.updatedData.course_id
              };
            }
            return asana;
          })
        };
      });
      queryClient.invalidateQueries(['asanas', courseid], { refetchType: 'all' });
    },
    onError: (err) => {
      console.error('Bulk import error:', err);
      toast.error(err?.response?.data?.message || 'Failed to update asanas');
    }
  });

  // --- Handlers ---
  const handleChangePostureName = (index, value) => {
    const updatedNames = [...postureNames];
    updatedNames[index] = value;
    setPostureNames(updatedNames);
  };

  const handleSave = (index) => {
    const updatedName = postureNames[index];
    const postureId = asanas[index].postureId;
    updatePostureNameMutation.mutate({
      postureid: postureId,
      posturename: updatedName
    });
  };

  const handleSelectAsana = (selectedLibAsana, idx) => {
    const postureId = asanas[idx].postureId;
    console.log('handleSelectAsana:', { asanaId: postureId, libraryAsanaId: selectedLibAsana.id, courseId: Number(courseid) });
    updateSingleAsanaMutation.mutate({
      asanaId: postureId,
      libraryAsanaId: selectedLibAsana.id,
      courseId: Number(courseid)
    });
  };

  const handleBulkSelect = (selectedLibAsanas) => {
    const mappings = selectedLibAsanas
      .map((libAsana, idx) => {
        if (!libAsana || !asanas[idx]) return null;
        return {
          asanaId: asanas[idx].postureId,
          libraryAsanaId: libAsana.id,
          courseId: Number(courseid)
        };
      })
      .filter(Boolean);
    console.log('Bulk import mappings:', mappings);
    importBulkAsanaMutation.mutate({ mappings });
  };

  const getStatusProps = (asanaStatus) => {
    switch (asanaStatus) {
      case 1:
        return { label: 'Approved', color: 'success', bgColor: '#e8f5e9' };
      case 2:
        return { label: 'Rejected', color: 'error', bgColor: '#ffebee' };
      default:
        return { label: 'Pending', color: 'warning', bgColor: '#fff3e0' };
    }
  };

  const courseName = data?.response?.[0]?.courseName || '';

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // --- Render ---
  return (
    <Box sx={{ minHeight: 'auto', p: { xs: 2, md: 4 } }}>
      <Box
        sx={{
          mb: 4,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 3,
          flexWrap: 'wrap'
        }}
      >
        <Box sx={{ textAlign: { sm: 'left' } }} />
        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange} centered>
            <Tab label="Asanas" {...a11yProps(0)} />
            <Tab label="Pranayammas" {...a11yProps(1)} />
            <Tab label="Relaxation" {...a11yProps(2)} />
          </Tabs>
        </Box>

        <Box
          sx={{
            flex: 1,
            textAlign: 'center'
          }}
        >
          <Typography variant="h5" sx={{ color: '#1a237e', fontWeight: 600 }}>
            Course:{' '}
            <Box component="span" sx={{ color: '#3f51b5' }}>
              {courseName}
            </Box>
          </Typography>
          <Typography variant="h6" sx={{ color: '#546e7a', mt: 1 }}>
            {tabValue === 0 ? `Total Asanas: ${asanas.length}` : 'Pranayammas'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flex: 1, justifyContent: { xs: 'center', sm: 'flex-end' } }}>
          <Button variant="contained" color="primary" startIcon={<SelfImprovementOutlined />} onClick={() => setCreateMedOpen(true)}>
            Create Pranayamma
          </Button>

          <Button variant="contained" color="primary" startIcon={<FileUploadOutlined />} onClick={() => setBulkOpen(true)}>
            Import Bulk Asanas
          </Button>
        </Box>

        <CreatePranayamaDialog
          open={createMedOpen}
          onClose={() => setCreateMedOpen(false)}
          onSave={(pranayamaData) => {
            console.log('New pranayama data:', pranayamaData);
          }}
        />

        <BulkImageImportDialog
          open={bulkOpen}
          onClose={() => setBulkOpen(false)}
          libraryAsanas={libraryData?.response || []}
          onBulkSelect={handleBulkSelect}
          maxSelect={asanas.length}
        />
      </Box>

      {/* Asanas Tab */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          {asanas.map((posture, index) => {
            const isTrained = posture.landmarks && JSON.parse(posture.landmarks || '[]').length > 0;
            const isEditing = editingIndex === index;
            const statusProps = getStatusProps(posture.asanaStatus);
            const hasImage = Boolean(posture.image);

            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={posture.postureId || posture.posturenumber}>
                <Fade in timeout={500 + index * 100}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                      borderRadius: 2,
                      transition: 'transform 0.3s, box-shadow 0.3s'
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        {isEditing ? (
                          <>
                            <TextField
                              variant="standard"
                              fullWidth
                              value={postureNames[index]}
                              onChange={(e) => handleChangePostureName(index, e.target.value)}
                              sx={{ input: { fontWeight: 'bold', fontSize: '1.2rem', color: '#3f51b5' } }}
                            />
                            <Tooltip title="Save">
                              <IconButton onClick={() => handleSave(index)} sx={{ color: '#2e7d32' }}>
                                <SaveOutlined />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 'bold',
                                textTransform: 'capitalize',
                                color: '#3f51b5',
                                flexGrow: 1,
                                fontSize: '1.2rem'
                              }}
                            >
                              {postureNames[index]}
                            </Typography>
                            <Tooltip title="Edit name">
                              <IconButton onClick={() => setEditingIndex(index)} sx={{ color: '#1976d2' }}>
                                <EditOutlined />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Chip
                          label={statusProps.label}
                          sx={{
                            fontWeight: 'bold',
                            backgroundColor: statusProps.bgColor,
                            color: statusProps.color + '.main',
                            '&:hover': { transform: 'scale(1.05)' }
                          }}
                          size="small"
                        />
                        {posture.asanaStatus === 2 && (
                          <Tooltip title={`Rejection Reason: ${posture?.rejection_reason}`}>
                            <IconButton sx={{ color: '#d32f2f', p: 0.5 }}>
                              <InfoOutlined style={{ fontSize: '1rem' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          color: isTrained ? '#2e7d32' : '#d32f2f'
                        }}
                      >
                        {isTrained ? 'Trained' : 'Not Trained'}
                      </Typography>
                      {hasImage && (
                        <Box sx={{ mt: 2 }}>
                          <img src={posture.image} alt={postureNames[index]} style={{ width: '100%', height: 'auto', borderRadius: 4 }} />
                        </Box>
                      )}
                    </CardContent>
                    <CardActions sx={{ pt: 0, pb: 2, px: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                     <Button
  size="small"
  variant="contained"
  onClick={() => {
    navigate('/create-pose', {
      state: {
        posture: {
          postureId: posture.postureId,        // Important
          posturename: postureNames[index],
          posturenumber: posture.posturenumber,
          landmarks: JSON.parse(posture.landmarks || '[]'),
          courseId: Number(courseid),
          status: posture.asanaStatus,
          image: posture.image,
          videoUrl: posture.videoUrl || posture.asanaVideo || posture.video // Add this
        }
      }
    });
  }}
  sx={{ fontWeight: 'bold', textTransform: 'none', py: 1, flex: 1 }}
>
  {isTrained ? 'Re-Train' : 'Train'}
  <ArrowRightOutlined style={{ marginLeft: 8 }} />
</Button>
                      {isTrained  && (
                        <Button
                          size="small"
                          variant="outlined"
                          sx={{ fontWeight: 'bold', textTransform: 'none', py: 1, flex: 1 }}
                          onClick={() => window.open(posture.video, '_blank')}
                        >
                          View
                          <EyeOutlined style={{ marginLeft: 8 }} />
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<FileUploadOutlined />}
                        sx={{
                          fontWeight: 'bold',
                          textTransform: 'none',
                          py: 1,
                          flexBasis: '100%',
                          width: '100%',
                          margin: '0px !important'
                        }}
                        onClick={() => {
                          console.log('Opening dialog for index:', index, 'postureId:', asanas[index].postureId);
                          setDialogIndex(index);
                        }}
                      >
                        Import Asana
                      </Button>
                      {dialogIndex === index && (
                        <ImportAsanaDialogs
                          open
                          onClose={() => setDialogIndex(null)}
                          asanas={libraryData?.response ?? []}
                          onSelect={(selectedLibAsana) => {
                            handleSelectAsana(selectedLibAsana, index);
                            setDialogIndex(null);
                          }}
                          isLoading={libraryLoading}
                          isError={libraryError}
                        />
                      )}
                    </CardActions>
                  </Card>
                </Fade>
              </Grid>
            );
          })}
        </Grid>
      </TabPanel>

      {/* Pranayammas Tab - placeholder for now */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ textAlign: 'center' }}>
          <ViewPranayamaPage />
        </Box>
      </TabPanel>


        {/* Relaxation Tab - placeholder for now */}
      <TabPanel value={tabValue} index={2}>
        <Box sx={{ textAlign: 'center' }}>
          <ViewRelaxationPage />
        </Box>
      </TabPanel>
    </Box>
  );
}

export default ViewAsanaPage;
